
import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

const HeroSection = () => {
  const isMobile = useIsMobile();
  return (
    <section className="relative w-full bg-black">
      {/* Full-width black background container */}
      <div className="hero-container relative overflow-hidden">
        {/* Background overlay without blur or heavy filters */}
        <div className="absolute inset-0">
          <div className="absolute inset-0">
            <img src="https://ik.imagekit.io/cbp/production/wp-content/uploads/2024/06/header-etf-tracker.jpg" alt="Fond patrimonial" className="w-full h-full object-cover object-center" />
          </div>
          
          {/* Gold accent diagonal line without blur */}
          <div className="absolute -right-40 top-0 bottom-0 w-[600px] bg-patrimoine-gold/20 transform rotate-12"></div>
          
          {/* Simplified gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/80 to-transparent opacity-70"></div>
          
          {/* Subtle grid pattern - keeping this for texture but with reduced opacity */}
          <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(#E9C46A 1px, transparent 1px), linear-gradient(to right, #E9C46A 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        </div>

        {/* Main hero content with improved layout */}
        <div className="relative min-h-[100vh] flex items-center">
          <div className="container mx-auto px-4 sm:px-6 py-20 sm:py-40">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Left column: Main content */}
              <div className="animate-fade-in space-y-8">
                {/* Distinctive badge */}
                <div className="inline-flex items-center rounded-full bg-patrimoine-gold/20 border border-patrimoine-gold/30 px-4 py-2 text-sm font-medium text-patrimoine-gold">
                  <Sparkles className="mr-2 h-4 w-4" />
                  <span>Stratégies d'investissement premium</span>
                </div>
                
                <h1 className="font-sans text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight">
                  <span className="text-patrimoine-gold block">Optimisez</span> 
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-patrimoine-gold/70">votre capital d'investissement</span>
                </h1>
                
                <p className="text-white/80 text-lg sm:text-xl md:text-2xl max-w-xl font-light leading-relaxed">
                  Des <span className="text-patrimoine-gold font-semibold">solutions d'investissement diversifiées</span> pour faire fructifier votre patrimoine et sécuriser votre avenir financier
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link to="/bilan-patrimonial">
                    <Button className="bg-patrimoine-gold hover:bg-amber-300 text-black font-medium text-base sm:text-lg py-6 px-8 rounded-lg shadow-[0_5px_30px_rgba(233,196,106,0.6)] transition-all duration-300 hover:scale-105 group">
                      Analyse de portefeuille offerte
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white font-medium text-base sm:text-lg py-6 px-8 rounded-lg transition-all duration-300">
                      Conseils en investissement
                    </Button>
                  </Link>
                </div>
              </div>
              
              {/* Right column: Visual element (only on large screens) */}
              <div className="hidden lg:flex justify-center items-center relative">
                <div className="relative w-[400px] h-[400px]">
                  {/* Gold circle with the new image inside */}
                  <div className="absolute inset-0 rounded-full overflow-hidden bg-patrimoine-gold/20 border border-patrimoine-gold/30">
                    <img 
                      src="/lovable-uploads/3babb6a0-e317-47f2-89d9-cc87d370e15a.png" 
                      alt="LA PATRIMONIALE" 
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  
                  {/* Stats floating around - removed backdrop-blur */}
                  <div className="absolute top-10 -left-10 bg-white/10 p-4 rounded-xl border border-white/20 shadow-xl">
                    <p className="text-patrimoine-gold font-bold text-3xl">+15%</p>
                    <p className="text-white/80 text-sm">Performance annuelle</p>
                  </div>
                  
                  <div className="absolute bottom-20 -right-10 bg-white/10 p-4 rounded-xl border border-white/20 shadow-xl">
                    <p className="text-patrimoine-gold font-bold text-3xl">97%</p>
                    <p className="text-white/80 text-sm">Satisfaction client</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-bounce">
          <div className="h-10 w-1 border-l-2 border-patrimoine-gold/50"></div>
          <div className="h-3 w-3 rounded-full bg-patrimoine-gold mt-1"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
